/* ===================================================================
   reauth -- transparent rr_ token recovery for the api/v1 client.

   Wires request()'s 401 recovery hook (registerTokenRefresher in ./_http)
   to a real re-mint: read the CURRENT Supabase session and exchange its JWT
   for a fresh rr_ data token -- the SAME exchange useSessionToken runs on
   login (coreApi.exchangeSessionToken), factored here so request() can call
   it without duplicating the endpoint or creating an import cycle.

   Two outcomes:
     - Supabase session ALIVE  -> mint a fresh rr_ token and return it;
       request() retries the original call once and the owner sees no error.
     - Supabase session DEAD/absent (or the JWT is itself rejected) -> clear
       the rr_ token AND sign out, so SessionProvider fires SIGNED_OUT and the
       route guard sends the owner to /auth with an honest "session expired"
       state -- instead of stranding them firing doomed 401s in onboarding
       (the 2026-07-12 incident: zero exchange calls all day, every
       authenticated call 401'd, UI stuck on "try again in a moment").

   Deduped: concurrent 401s share ONE in-flight re-mint (no revoke storm).
   =================================================================== */
import { supabase } from '../supabase';
import { coreApi } from './core';
import { ApiError, setToken, registerTokenRefresher } from './_http';

let _inFlight: Promise<string | null> | null = null;

/* Recovery is impossible: the Supabase session is dead/absent. Clear the rr_
   token and sign out locally so onAuthStateChange fires SIGNED_OUT, the
   SessionProvider drops the session, and the route guard redirects to /auth.
   signOut() is best-effort + idempotent even with no live session remaining. */
async function _deadSession(): Promise<null> {
  setToken(null);
  try {
    await supabase.auth.signOut();
  } catch {
    /* offline / already signed out -- the setToken(null) above still stands */
  }
  return null;
}

async function _remint(): Promise<string | null> {
  let jwt: string | undefined;
  try {
    // getSession() refreshes the JWT if the refresh token is still valid; a
    // null result here means the session is genuinely dead (refresh expired).
    const { data } = await supabase.auth.getSession();
    jwt = data.session?.access_token;
  } catch {
    jwt = undefined;
  }
  if (!jwt) return _deadSession();

  try {
    const res = await coreApi.exchangeSessionToken(jwt);
    setToken(res.api_key);
    return res.api_key;
  } catch (e) {
    // The Supabase JWT was rejected (expired between read and exchange, or the
    // user is no longer authorized) -> treat as a dead session and route out.
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      return _deadSession();
    }
    // Transient (network / 5xx) or not-yet-linked (409): clear the dead rr_
    // token but do NOT sign out -- a later action can retry cleanly.
    setToken(null);
    return null;
  }
}

/** Mint a fresh rr_ token from the current Supabase session, or null if the
    session is dead (in which case the user is also signed out). Concurrent
    callers share one in-flight re-mint so a burst of 401s cannot start a
    revoke storm. */
export function remintSessionToken(): Promise<string | null> {
  if (_inFlight) return _inFlight;
  _inFlight = _remint().finally(() => {
    _inFlight = null;
  });
  return _inFlight;
}

// Install as request()'s 401 recovery hook at import time (side-effect import
// from lib/api/index.ts).
registerTokenRefresher(remintSessionToken);
